import React, { useMemo, memo } from "react";
import { CgProfile } from "react-icons/cg";
import { MdFileDownload } from "react-icons/md";
import { TbFileOff } from "react-icons/tb";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "react-bootstrap/Table";
import PageHeader from "@features/shared/components/PageHeader";
import QueryUpdate from "./QueryUpdate";
import { useFileHandler } from "../hooks";
import "@components/Queries/QueryDetails.css";

/**
 * FileList component - displays uploaded files with download functionality
 */
const FileList = memo(({ files, onDownload }) => {
  if (!files || files.length === 0) {
    return (
      <div className="queryDetails__orderDetailsProfileSection">
        <TbFileOff style={{ color: "#3901FF" }} />
        <p className="queryDetails__orderDetailsProfileRelated">No file attached</p>
      </div>
    );
  }

  return (
    <div className="queryDetails__orderDetailsProfileSection">
      {files.map((item) => (
        <button
          key={item?.ContentDocument?.LatestPublishedVersionId}
          onClick={() => onDownload(
            item?.ContentDocument?.LatestPublishedVersionId,
            item?.ContentDocument?.Title
          )}
          className="queryDetails__orderDetailsProfileRelated"
        >
          <p style={{ fontSize: "9px", margin: "0px", color: "#808080" }}>
            Download
          </p>
          <MdFileDownload style={{ height: "2.1em", width: "2.1em", color: "#3901FF" }} />
          {item?.ContentDocument?.Title}
        </button>
      ))}
    </div>
  );
});

FileList.displayName = "FileList";

/**
 * QueryDetails component - displays detailed view of a service request
 */
const QueryDetails = () => {
  const params = useParams();
  const accessToken = useSelector((state) => state?.tokens?.accessToken);

  // Find query info from Redux state
  const queryInfo = useSelector(
    (state) => state?.query?.queryDetails?.find((info) => info?.Id === params?.id),
    (prev, next) => prev?.Id === next?.Id
  );

  // Use file handler hook
  const { uploadedFiles, handleDownload } = useFileHandler(params?.id, accessToken);

  // Memoize display name
  const displayName = useMemo(() => {
    return queryInfo?.recType === "Dealer"
      ? queryInfo?.dealerName
      : queryInfo?.customerName;
  }, [queryInfo]);

  // Memoize date and time
  const dateTime = useMemo(() => {
    const parts = queryInfo?.createdDate?.split(" ") || [];
    return {
      date: parts[0] || "",
      time: parts[1] && parts[2] ? `${parts[1]} ${parts[2]}` : "",
    };
  }, [queryInfo?.createdDate]);

  return (
    <>
      <PageHeader title="Service Request" path="/Queries" />
      <div className="queryDetails">
        <h6 className="queryDetails__name">Query Details</h6>
        <div className="queryDetails__box">
          <div className="queryDetails__header">
            <CgProfile className="queryDetails__profile" />
            <div className="queryDetails__detailName">
              <div className="queryDetails__profileName">{displayName}</div>
              <p className="queryDetails__profileSubName">{queryInfo?.CaseNumber}</p>
            </div>
          </div>

          <Table style={{ marginTop: "10px" }}>
            <tbody className="queryDetails__details" style={{ borderStyle: "hidden" }}>
              <tr className="queryDetails__subjectSection">
                <td>Subject:</td>
                <td>{queryInfo?.Subject}</td>
              </tr>
              <tr>
                <td>Description:</td>
                <td>{queryInfo?.Description}</td>
              </tr>
              {queryInfo?.issueResolutionComments && (
                <tr>
                  <td>Issue Resolution Comments:</td>
                  <td>{queryInfo?.issueResolutionComments}</td>
                </tr>
              )}
            </tbody>
          </Table>

          <h6 className="queryDetails__orderDetailsName">Other Details</h6>
          <div className="queryDetails__orderDetailsSection">
            <div className="queryDetails__orderDetailsTable">
              <FileList files={uploadedFiles} onDownload={handleDownload} />

              <div className="queryDetails__datetime">
                <p className="queryDetails__orderDetailsTitle">Date</p>
                <p className="queryDetails__orderDetailsData">{dateTime.date}</p>
                <p className="queryDetails__orderDetailsTitle">Time</p>
                <p className="queryDetails__orderDetailsData">{dateTime.time}</p>
              </div>

              <div className="queryDetails__statusRelated">
                <p className="queryDetails__orderDetailsTitle">Status</p>
                <p className="queryDetails__orderDetailsData" style={{ color: "#2CC26A" }}>
                  {queryInfo?.Status}
                </p>
                <p className="queryDetails__orderDetailsTitle">Related to</p>
                <p className="queryDetails__orderDetailsData">{queryInfo?.recType}</p>
              </div>
            </div>
          </div>

          <div className="row m-3">
            <div className="col-6 p-0"></div>
          </div>
        </div>
      </div>
      <QueryUpdate recId={queryInfo?.Id} />
    </>
  );
};

export default memo(QueryDetails);
